/**
 * Dashboard types for executive dashboard
 * Maps to Requirement 76: Web UI — Executive Dashboard [MVP]
 */

/**
 * Time period for dashboard data
 */
export enum TimePeriod {
  LAST_7_DAYS = '7d',
  LAST_30_DAYS = '30d',
  LAST_90_DAYS = '90d',
  LAST_12_MONTHS = '12m',
  LAST_24_MONTHS = '24m',
  CUSTOM = 'custom',
}

/**
 * Dashboard role-based view types
 * AC 2: Support role-based views
 */
export enum DashboardView {
  CFO = 'cfo', // Executive summary
  FINOPS = 'finops', // Operational detail
  ENGINEERING = 'engineering', // Service-scoped
  FINANCE = 'finance', // Allocation/chargeback
}

/**
 * Total spend vs budget metrics
 * AC 1: Total spend vs budget
 * REQ-001: Real Budget Management
 */
export interface SpendVsBudget {
  /** Spend over the SELECTED viewing window (7/30/90 days, 12/24 months). */
  currentSpend: number;
  /**
   * Spend for the budget's own period - this calendar month, 1st to today.
   * Distinct from currentSpend on purpose: a budget is a monthly commitment,
   * so variance must be month-vs-month. Comparing a 90-day window against a
   * monthly budget always read as wildly over, and a 7-day window always as
   * comfortably under, regardless of actual performance.
   *
   * Scoped to the BUDGETED accounts when `budgetCoverageKnown` is true. The
   * budget is a sum of per-account budgets, so the spend it is measured
   * against has to be the spend of those same accounts - otherwise a partly
   * budgeted tenant compares a small budget with the whole bill.
   */
  budgetPeriodSpend?: number;
  /** MTD spend across every account in view, budgeted or not. */
  totalMonthSpend?: number;
  /**
   * How many accounts `budgetAmount` covers. Larger than the number of budgets
   * when one of them is set on a payer account, since a payer budget covers its
   * member accounts too (their spend is where a consolidated CUR puts it).
   */
  budgetedAccounts?: number;
  /** How many of those actually reported spend this month. */
  budgetedAccountsWithSpend?: number;
  /** MTD spend in accounts that have no budget - excluded from the pace. */
  unbudgetedSpend?: number;
  /**
   * False when no budgeted account could be matched to the cost breakdown, in
   * which case `budgetPeriodSpend` falls back to total spend and the pace is
   * approximate rather than silently reading 100% under.
   */
  budgetCoverageKnown?: boolean;
  /** The budget's period, as YYYY-MM - so the UI can label the comparison. */
  budgetPeriod?: string;
  budgetAmount: number | null; // null when not configured
  budgetConfigured: boolean; // AC-001.2: Indicate if budget is configured
  /**
   * Budget pro-rated to the days elapsed this month.
   *
   * Variance is measured against THIS, not against the full monthly budget:
   * month-to-date spend compared to a whole month's budget reads "under" for
   * most of the month no matter how fast money is actually going out, which
   * hides an overspend until it is too late to act on.
   */
  proRatedBudget?: number;
  /** Straight-line projection of month-to-date spend to month end. */
  projectedMonthEnd?: number;
  /** projectedMonthEnd - budgetAmount. Positive means heading over. */
  projectedOverage?: number;
  daysElapsed?: number;
  daysInMonth?: number;
  /** Variance of MTD spend against the PRO-RATED budget (i.e. pace). */
  variance: number | null; // null when budget not configured
  variancePercentage: number | null; // null when budget not configured
  varianceStatus?: 'safe' | 'warning' | 'danger' | 'critical' | null; // Visual indicator
  forecastedSpend: number;
  currency: string;
}

/**
 * Month-over-month trend data
 * AC 1: Month-over-month trend
 */
export interface MoMTrend {
  currentMonth: number;
  previousMonth: number;
  change: number;
  changePercentage: number;
  trend: 'up' | 'down' | 'stable';
  monthlyData: MonthlyDataPoint[];
  /** True when previousMonth is a rough guess (real data unavailable), not a measured figure. */
  isPreviousMonthEstimated?: boolean;
}

export interface MonthlyDataPoint {
  month: string;
  spend: number;
  budget?: number;
  /** Real per-provider cost for this specific month, aggregated from the
   * daily time series' own by_provider field - drives SpendChart's stacked
   * bars so each month's segments reflect that month's actual mix instead
   * of one fixed ratio replayed across every bar. */
  by_provider?: Record<string, number>;
}

/**
 * Realized savings metrics
 * AC 1: Realized savings
 */
export interface RealizedSavings {
  totalSavings: number;
  savingsPercentage: number;
  savingsByCategory: SavingsByCategory[];
  monthlyTrend: MonthlySavings[];
}

export interface SavingsByCategory {
  category: string;
  amount: number;
  percentage: number;
}

export interface MonthlySavings {
  month: string;
  amount: number;
}

/**
 * Forecast accuracy metrics
 * AC 1: Forecast accuracy
 * REQ-002: ML-Based Forecasting
 */
export interface ForecastAccuracy {
  accuracyPercentage: number;
  forecastedAmount: number; // p50 value
  actualAmount: number;
  variance: number;
  lastUpdated: string;
  // REQ-002 AC-002.3: Display "Projected: $X (range: $Y-$Z)" with confidence indicator
  p10?: number; // Optimistic (10th percentile)
  p50?: number; // Expected (50th percentile/median)
  p90?: number; // Pessimistic (90th percentile)
  confidence?: number; // 0.00 to 1.00
  pattern?: 'linear' | 'exponential' | 'seasonal' | 'stable';
}

/**
 * Commitment coverage and utilization
 * AC 1: Commitment coverage/utilization
 */
export interface CommitmentMetrics {
  coverage: CommitmentCoverage;
  utilization: CommitmentUtilization;
  commitmentsByType: CommitmentByType[];
  /** REQ-012-HYBRID: Per-provider commitment breakdown */
  providerBreakdown?: ProviderCommitmentBreakdown;
}

/**
 * Per-provider commitment breakdown
 * REQ-012-HYBRID: Show AWS Savings Plans, Azure Reservations, GCP CUDs separately
 */
export interface ProviderCommitmentBreakdown {
  aws?: ProviderCommitmentData;
  azure?: ProviderCommitmentData;
  gcp?: ProviderCommitmentData;
}

/**
 * Commitment data for a single provider
 */
export interface ProviderCommitmentData {
  /** Provider-specific commitment type label (e.g., "Savings Plans", "Reservations", "CUDs") */
  commitmentType: string;
  /** Coverage percentage (0-100) */
  coveragePercentage: number;
  /** Utilization percentage (0-100) */
  utilizationPercentage: number;
  /** Whether real data is available for this provider */
  dataAvailable: boolean;
}

export interface CommitmentCoverage {
  coveragePercentage: number;
  coveredSpend: number;
  uncoveredSpend: number;
}

export interface CommitmentUtilization {
  utilizationPercentage: number;
  usedAmount: number;
  committedAmount: number;
  wasteAmount: number;
}

export interface CommitmentByType {
  type: 'savings_plan' | 'reserved_instance' | 'committed_use_discount' | 'reservation';
  coverage: number;
  utilization: number;
  spend: number;
}

/**
 * Waste index metrics
 * AC 1: Waste index
 * Fields are nullable to support empty state when optimization API is unavailable
 */
export interface WasteIndex {
  wasteScore: number | null; // 0-100, lower is better. null = no data
  totalWaste: number | null;
  wastePercentage: number | null;
  wasteByCategory: WasteByCategory[];
  dataAvailable: boolean; // false when optimization API data is unavailable
}

export interface WasteByCategory {
  category: string;
  amount: number;
  percentage: number;
  description: string;
}

/**
 * Top anomalies
 * AC 1: Top anomalies
 */
export interface TopAnomaly {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  detectedAt: string;
  scope: string;
  deltaAmount: number;
  deltaPercentage: number;
  status: 'open' | 'investigating' | 'resolved' | 'dismissed';
  provider?: 'aws' | 'azure' | 'gcp';
}

/**
 * Complete executive dashboard data
 * Aggregates all dashboard metrics
 */
export interface ExecutiveDashboard {
  /**
   * Non-fatal problems the API reported alongside the figures — typically a
   * provider whose credentials could not be read, so its costs are missing
   * rather than genuinely zero.
   *
   * Comes from the response envelope's `errors` array, which the API has
   * always carried and the UI never read. Without it a broken provider and an
   * un-onboarded one both render as $0.00 with nothing to tell them apart.
   */
  dataWarnings?: Array<{ code: string; message: string }>;
  spendVsBudget: SpendVsBudget;
  momTrend: MoMTrend;
  realizedSavings: RealizedSavings;
  forecastAccuracy: ForecastAccuracy;
  commitmentMetrics: CommitmentMetrics;
  wasteIndex: WasteIndex;
  topAnomalies: TopAnomaly[];
  /** Count the anomalies API hid via its default filter (informational
   * `new_service` findings), so the widget can say so rather than showing a
   * bare "No anomalies detected" while real findings exist. */
  hiddenAnomalyCount?: number;
  costByRegion?: Array<{ region: string; cost: number; percentage: number }>;
  costByService?: Array<{ service: string; cost: number; percentage: number }>;
  costByAccount?: Array<{ account: string; cost: number; percentage: number }>;
  costByProvider?: Array<{ provider: string; cost: number; percentage: number }>;
  currency?: string; // Currency code (USD, INR, etc.)
  lastUpdated: string;
}

/**
 * Dashboard query parameters
 */
export interface DashboardQueryParams {
  timePeriod: TimePeriod;
  view?: DashboardView;
  startDate?: string;
  endDate?: string;
  tenantId?: string;
  /** Optional provider filter: 'aws', 'azure', 'gcp'. Omit for all providers. */
  provider?: string;
}

/**
 * Dashboard API response
 */
export interface DashboardResponse {
  data: ExecutiveDashboard;
  metadata: {
    queryTime: number;
    dataFreshness: string;
    scope: string;
  };
}

/**
 * Per-provider summary data from cost API
 * REQ-012-HYBRID: Per-provider metrics for dashboard provider summary cards
 */
export interface ProviderSummaryData {
  total_cost: number;
  currency: string;
  period_start: string;
  period_end: string;
  providers: Record<string, {
    cost: number;
    percentage: number;
    mom_change: number;
    mom_trend: 'up' | 'down';
    waste_percentage: number;
    savings_opportunity: number;
    top_services: Array<{ service: string; cost: number }>;
    connection_status: 'connected' | 'disconnected' | 'error';
    currency: string;
  }>;
}
